import { useRef, useState } from "react";
import {
  Camera,
  Check,
  ChevronDown,
  Copy,
  Gamepad2,
  User,
} from "lucide-react";

export const MINT = "#10b981";
export const PURPLE = "#a855f7";

export function SettingsTabs({ tabs, value, onChange }) {
  return (
    <div className="sticky top-2 z-30 mx-auto w-full max-w-2xl px-1">
      <div className="relative rounded-2xl border border-white/10 bg-black/60 p-1 backdrop-blur-xl shadow-[0_8px_40px_-12px_rgba(168,85,247,0.35)]">
        <div className="flex gap-1">
          {tabs.map((t) => {
            const active = value === t.id;
            return (
              <button
                key={t.id}
                onClick={() => onChange(t.id)}
                className={`relative flex-1 rounded-xl px-3 py-2.5 text-xs font-semibold uppercase tracking-wider transition sm:text-sm ${
                  active ? "text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {active && (
                  <span
                    className="absolute inset-0 rounded-xl"
                    style={{
                      background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                      boxShadow: `0 8px 24px -10px ${MINT}`,
                    }}
                  />
                )}
                <span className="relative">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SettingsCard({ children, glow = "purple", className = "" }) {
  const glowColor = glow === "mint" ? MINT : PURPLE;
  return (
    <div
      className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl sm:p-6 ${className}`}
      style={{
        boxShadow: `inset 0 1px 0 0 rgba(255,255,255,0.05), 0 20px 60px -30px ${glowColor}66`,
      }}
    >
      <div
        className="pointer-events-none absolute -top-24 -right-20 h-48 w-48 rounded-full blur-3xl opacity-30"
        style={{ background: glowColor }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

export function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-4 flex items-start gap-3">
      {icon && (
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5"
          style={{ color: MINT }}
        >
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <h2 className="text-base font-semibold tracking-tight text-white">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-xs text-white/50">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

const baseInput =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-[15px] text-white placeholder:text-white/30 outline-none transition focus:border-emerald-400/40 focus:bg-white/[0.06] focus:ring-2 focus:ring-emerald-500/20";

export function InputField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  readOnly = false,
  hint,
}) {
  return (
    <label className="block">
      {label && (
        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/60">
          {label}
        </span>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`${baseInput} ${readOnly ? "cursor-not-allowed opacity-70" : ""}`}
      />
      {hint && <span className="mt-1 block text-[11px] text-white/40">{hint}</span>}
    </label>
  );
}

export function TextareaField({ label, value, onChange, placeholder, rows = 4 }) {
  return (
    <label className="block">
      {label && (
        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/60">
          {label}
        </span>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={`${baseInput} resize-none leading-relaxed`}
      />
    </label>
  );
}

export function SocialInput({ icon, platform, value, onChange, placeholder }) {
  return (
    <div className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5 transition focus-within:border-emerald-400/40 focus-within:bg-white/[0.06]">
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/5 text-white/70 transition group-focus-within:text-white"
        style={{ boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.06)` }}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
          {platform}
        </p>
        <input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm text-white placeholder:text-white/30 outline-none"
        />
      </div>
    </div>
  );
}

/* ---------- Upload Avatar ---------- */
export function UploadAvatarCard({ photo, onPick }) {
  const fileRef = useRef(null);
  return (
    <SettingsCard glow="purple">
      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="relative h-24 w-24 shrink-0 overflow-hidden rounded-full"
          style={{
            background: `conic-gradient(from 140deg, ${PURPLE}, ${MINT}, ${PURPLE})`,
            padding: 2,
          }}
        >
          <span className="block h-full w-full overflow-hidden rounded-full bg-[#0b0a14]">
            {photo ? (
              <img src={photo} alt="Avatar" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-white/40">
                <User size={32} />
              </span>
            )}
          </span>
          <span
            className="absolute bottom-1 right-1 grid h-7 w-7 place-items-center rounded-full bg-black ring-1 ring-white/20"
            style={{ boxShadow: `0 0 16px ${MINT}66` }}
          >
            <Camera size={13} />
          </span>
        </button>
        <div className="flex-1 text-center sm:text-left">
          <h3 className="text-base font-semibold text-white">Profile photo</h3>
          <p className="mt-1 text-xs text-white/50">
            Square images look best. PNG or JPG, up to 5MB.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/10"
            >
              Upload new
            </button>
            {photo && (
              <button
                type="button"
                onClick={() => onPick?.(null)}
                className="rounded-lg px-3 py-2 text-xs font-semibold text-white/60 hover:text-white"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          onPick?.(f);
        }}
      />
    </SettingsCard>
  );
}

export function GameSelect({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" style={{marginBottom:"45%"}}>
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/60">
        Select your main game
      </span>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${baseInput} flex items-center justify-between text-left`}
      >
        <span className="flex items-center gap-2.5">
          <Gamepad2 size={16} style={{ color: PURPLE }} />
          <span className={`font-medium ${value ? "text-white" : "text-white/40"}`}>
            {value || "Choose a category"}
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`text-white/50 transition ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0914]/95 p-1.5 shadow-2xl backdrop-blur-xl">
          {options.length === 0 && (
            <div className="px-3 py-2.5 text-sm text-white/40">
              No categories available.
            </div>
          )}
          {options.map((g) => {
            const active = g === value;
            return (
              <button
                key={g}
                type="button"
                onClick={() => {
                  onChange?.(g);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition hover:bg-white/5 ${
                  active ? "text-white" : "text-white/70"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Gamepad2
                    size={14}
                    style={{ color: active ? MINT : "rgba(255,255,255,0.3)" }}
                  />
                  {g}
                </span>
                {active && <Check size={14} style={{ color: MINT }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ReferralCard({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };
  return (
    <SettingsCard glow="mint">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-white/50">
            Referral code
          </p>
          <p className="mt-1 font-mono text-lg font-bold text-white sm:text-xl">
            {code}
          </p>
          <p className="mt-1 text-xs text-white/50">
            Share with creators & editors to earn rewards.
          </p>
        </div>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/10"
        >
          {copied ? (
            <>
              <Check size={14} style={{ color: MINT }} /> Copied
            </>
          ) : (
            <>
              <Copy size={14} /> Copy
            </>
          )}
        </button>
      </div>
    </SettingsCard>
  );
}

export function SaveButton({ onClick, label = "Save changes" }) {
  return (
    <div className="sticky bottom-3 z-20 mt-6">
      <button
        type="button"
        onClick={onClick}
        className="group relative w-full overflow-hidden rounded-2xl px-5 py-4 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98]"
        style={{
          background: `linear-gradient(135deg, ${MINT}, #34d399)`,
          boxShadow: `0 16px 50px -12px ${MINT}, 0 0 0 1px rgba(255,255,255,0.1) inset`,
        }}
      >
        <span className="relative">{label}</span>
      </button>
    </div>
  );
}
